import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { setActiveProposal } from '@/lib/assistantAudit'
import { AssistantModel } from '@/http/models/assistant/Assistant'
import { AssistantProposal } from '@/types/assistant'
import { CouponFormState } from '@/components/features/coupons/couponFieldsConfig'

import { useAssistant } from './AssistantProvider'
import { useReviewStamp } from './useReviewStamp'

type CouponProposal = Extract<AssistantProposal, { kind: 'coupon_create' }>

/** A discount the assistant prepared (?proposal=…): prefill for the create dialog, and its outcome. */
export const useCouponProposal = (openCreateDialog: () => void) => {
  const [params, setParams] = useSearchParams()
  const proposalUid = params.get('proposal')
  const { markProposal } = useAssistant()
  const [proposal, setProposal] = useState<CouponProposal | null>(null)
  const stamp = useReviewStamp()

  useEffect(() => {
    if (!proposalUid) return
    AssistantModel.getProposal(proposalUid).then((res) => {
      const p = res.success ? res.data?.result : null
      if (!p || p.kind !== 'coupon_create' || p.status !== 'pending') {
        if (p && p.status !== 'pending') toast.info('This change was already handled.')
        setParams({}, { replace: true })
        return
      }
      setProposal((current) => (current?.proposal_uid === p.proposal_uid ? current : p))
      openCreateDialog()
    })
    // openCreateDialog is recreated each render by its hook; the proposal id decides when to open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalUid, setParams, stamp])

  // Stable while the proposal is the same, so the dialog does not reset what the merchant edits.
  const prefill = useMemo<Partial<CouponFormState> | undefined>(() => (proposal ? {
    coupon_code: proposal.payload.coupon_code,
    description: proposal.payload.description ?? '',
    discount_type: proposal.payload.discount_type,
    discount: proposal.payload.discount,
    min_order_amount: proposal.payload.min_order_amount,
    max_discount_amount: proposal.payload.max_discount_amount ?? 0,
    usage_limit: proposal.payload.usage_limit ?? 1,
    expiry_date: proposal.payload.expiry_date,
    status: 'active',
  } : undefined), [proposal])

  // Saves made while this proposal is on screen are tagged with it in the audit log.
  useEffect(() => {
    if (!proposal) return
    setActiveProposal(proposal.proposal_uid)
    return () => setActiveProposal(null)
  }, [proposal])

  const finish = useCallback((status: 'published' | 'discarded') => {
    if (!proposal) return
    markProposal(proposal.proposal_uid, status)
    setProposal(null)
    setParams({}, { replace: true })
  }, [markProposal, proposal, setParams])

  return { proposal, prefill, onSaved: () => finish('published'), discard: () => finish('discarded') }
}
